import { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Button } from '@antimatterhq/ui/components/button';
import { core_v1_Transform } from '@antimatterhq/asl-api-ts-v1';
import { useDeleteTransform } from '@/hooks/core/useDeleteTransform';
import { useGetTransformsInfinite } from '@/hooks/core/useGetTransformsInfinite';

interface DeleteTransformDialogProps {
  transform?: core_v1_Transform;
  onClose: () => void;
  'data-testid'?: string;
}

export function DeleteTransformDialog(props: DeleteTransformDialogProps) {
  const testId = props['data-testid'] || 'delete-transform-dialog';
  const { mutateAsync: deleteTransformAsync, isPending } = useDeleteTransform();
  const { optimisticDelete } = useGetTransformsInfinite();

  const onOpenChange = (open: boolean) => {
    if (!open) {
      props.onClose();
    }
  };

  const handleDeleteRule = useCallback(async () => {
    if (!props.transform?.metadata?.name) return;

    await deleteTransformAsync(
      { name: props.transform.metadata?.name },
      {
        toasts: {
          success: `${props.transform.metadata?.name} deleted successfully`,
          error: (error) =>
            `Failed to delete ${props.transform?.metadata?.name}. "${error.response?.statusText}"`,
        },
        onSuccess: async () => {
          props.onClose();
          optimisticDelete(props.transform!);
        },
        onError: async ({ error }) => {
          // Not useful in this case, but ideally can be used to update form errors
          console.error(error);
        },
      },
    );
  }, [props, deleteTransformAsync, optimisticDelete]);

  return (
    <Dialog open={!!props.transform} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-full flex-col" data-testid={testId}>
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>
            Delete {props.transform?.metadata?.name || ''}?
          </DialogTitle>
        </DialogHeader>
        <div className="flex w-full justify-end gap-4">
          <Button
            onClick={props.onClose}
            variant={'ghost'}
            disabled={isPending}
            loading={isPending}
            data-testid={`${testId}-cancel-button`}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRule}
            variant={'danger'}
            disabled={isPending}
            loading={isPending}
            data-testid={`${testId}-delete-button`}
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
